
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";

import { applicationFormSchema, ApplicationFormValues } from "./application-form/formSchema";
import { PersonalInfoSection } from "./application-form/PersonalInfoSection";
import { CreativeApproachSection } from "./application-form/CreativeApproachSection";
import { MotivationSection } from "./application-form/MotivationSection";
import { AcceptanceSection } from "./application-form/AcceptanceSection";
import { useApplicationSubmit } from "./application-form/useApplicationSubmit";

interface ApplicationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challengeId?: string;
}

export function ApplicationFormDialog({ 
  open, 
  onOpenChange,
  challengeId = "lamour-surcote" 
}: ApplicationFormDialogProps) {
  // Form definition
  const form = useForm<ApplicationFormValues>({
    resolver: zodResolver(applicationFormSchema),
    defaultValues: {
      fullName: "",
      tiktokHandle: "",
      email: "",
      phoneNumber: "",
      birthDate: "",
      creativeApproach: "",
      motivation: "",
      acceptedContact: false,
    },
  });

  const handleFormReset = () => {
    form.reset();
    onOpenChange(false);
  };

  const { handleSubmit, isSubmitting } = useApplicationSubmit(handleFormReset, challengeId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/70">
            Rejoindre l'opération "L'amour c'est surcoté"
          </DialogTitle>
          <DialogDescription>
            Remplissez ce formulaire pour participer au challenge créatif.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
            <PersonalInfoSection form={form} />
            <CreativeApproachSection form={form} />
            <MotivationSection form={form} />
            <AcceptanceSection form={form} />

            <Button 
              type="submit" 
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer ma candidature"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
